import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { LucideIcon } from "lucide-react";
import { TrendingUp, Target, Users, Rocket, Circle } from "lucide-react";
import { CowIcon } from "@/components/icons/CowIcon";
import { whyUsContent } from "@/content/homepage";

interface Benefit {
  id: string;
  title: string;
  description: string;
  icon: string;
  display_order: number;
  active: boolean;
}

export const WhyUs = () => {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        const { data, error } = await supabase
          .from("cms_benefits")
          .select("*")
          .eq("active", true)
          .order("display_order", { ascending: true });

        if (error) throw error;
        setBenefits(data || []);
      } catch (error) {
        console.error("Error fetching benefits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBenefits();
  }, []);

  const iconMap: Record<string, LucideIcon> = {
    TrendingUp,
    Target,
    Users,
    Rocket,
    Circle,
    Cow: CowIcon as unknown as LucideIcon,
  };

  const getIcon = (iconName: string): LucideIcon => {
    return iconMap[iconName] || Circle;
  };

  const displayBenefits = benefits.length > 0 ? benefits : whyUsContent.benefits.map((b, i) => ({
    id: `fallback-${i}`,
    title: b.title,
    description: b.description,
    icon: ["TrendingUp", "Target", "Users"][i] || "Circle",
    display_order: i,
    active: true,
  }));

  return (
    <section className="py-24 md:py-32 bg-muted/20">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-4xl font-bold mb-6 text-foreground md:text-5xl">{whyUsContent.title}</h2>
          <p className="text-xl text-muted-foreground">{whyUsContent.subtitle}</p>
        </div>

        <div className="grid gap-10 md:grid-cols-3 mx-auto max-w-6xl">
          {displayBenefits.map((benefit, index) => {
            const IconComponent = getIcon(benefit.icon);
            return (
              <div key={benefit.id || index} className="text-center">
                <div className="mb-6 mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
                  <IconComponent className="h-8 w-8 text-accent" />
                </div>
                <h3 className="mb-4 text-2xl font-bold text-foreground">
                  {benefit.title}
                </h3>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
